import { Match, Participant, Tournament } from '../models';
import { TournamentRepository, ParticipantRepository, MatchRepository } from '../repositories';

interface GeneratedBracket {
  tournament: Tournament;
  matches: Match[];
}

interface TempMatch {
  tournamentId: string;
  round: number;
  matchNumber: number;
  bracketType: 'winners' | 'losers' | 'second_losers' | 'grand_final';
  participant1Id?: string;
  participant2Id?: string;
  status: 'pending' | 'in_progress' | 'completed';
  nextMatchIndex?: number;
  loserNextMatchIndex?: number;
}

/**
 * Triple Elimination bracket generator — supports exactly 4 participants.
 *
 * Structure (8–9 matches total):
 *   WB R1 (2)  → WB R2/Final (1)  → Grand Final
 *   WB losers  → LB1 R1 (1)
 *   WB Final loser → LB1 R2/Final (1)  → LB_SEMI (second_losers)
 *   LB1 losers → LB2 (1)               → LB_SEMI (second_losers)
 *   LB_SEMI winner → Grand Final
 */
export class GenerateTripleEliminationBracketUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<GeneratedBracket> {
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament has already started or is completed');
    }

    const participants = await this.participantRepository.getByTournamentId(tournamentId);

    if (participants.length !== 4) {
      throw new Error('Triple Elimination requires exactly 4 participants');
    }

    const sorted = [...participants].sort((a, b) => {
      const seedA = a.seed ?? Infinity;
      const seedB = b.seed ?? Infinity;
      return seedA - seedB;
    });

    const tempMatches: TempMatch[] = [];

    // ── Winners Bracket ──────────────────────────────────────────────────────
    // WB R1: match 0 (P1 vs P2), match 1 (P3 vs P4)
    tempMatches.push({
      tournamentId, round: 1, matchNumber: 1, bracketType: 'winners', status: 'pending',
      participant1Id: sorted[0].id, participant2Id: sorted[1].id,
    });
    tempMatches.push({
      tournamentId, round: 1, matchNumber: 2, bracketType: 'winners', status: 'pending',
      participant1Id: sorted[2].id, participant2Id: sorted[3].id,
    });
    // WB R2 / Final: match 2
    tempMatches.push({
      tournamentId, round: 2, matchNumber: 1, bracketType: 'winners', status: 'pending',
    });

    // WB R1 winners → WB R2
    tempMatches[0].nextMatchIndex = 2;
    tempMatches[1].nextMatchIndex = 2;

    // ── Losers Bracket 1 ─────────────────────────────────────────────────────
    // LB1 R1: match 3 (WB R1 losers)
    tempMatches.push({
      tournamentId, round: 1, matchNumber: 1, bracketType: 'losers', status: 'pending',
    });
    // LB1 R2 / LB1 Final: match 4 (WB Final loser + LB1 R1 winner)
    tempMatches.push({
      tournamentId, round: 2, matchNumber: 1, bracketType: 'losers', status: 'pending',
    });

    // WB R1 losers → LB1 R1
    tempMatches[0].loserNextMatchIndex = 3;
    tempMatches[1].loserNextMatchIndex = 3;

    // WB Final loser → LB1 R2
    tempMatches[2].loserNextMatchIndex = 4;

    // LB1 R1 winner → LB1 R2
    tempMatches[3].nextMatchIndex = 4;

    // ── Losers Bracket 2 ─────────────────────────────────────────────────────
    // LB2 R1: match 5 (LB1 R1 loser + LB1 R2 loser)
    tempMatches.push({
      tournamentId, round: 1, matchNumber: 1, bracketType: 'second_losers', status: 'pending',
    });

    // LB1 R1 loser → LB2
    tempMatches[3].loserNextMatchIndex = 5;
    // LB1 R2 loser → LB2
    tempMatches[4].loserNextMatchIndex = 5;

    // ── Losers Semi-Final ─────────────────────────────────────────────────────
    // LB_SEMI: match 6 (LB1 champion + LB2 champion)
    // bracketType 'second_losers' — the GF logic identifies this as the "losers side" finalist
    tempMatches.push({
      tournamentId, round: 2, matchNumber: 1, bracketType: 'second_losers', status: 'pending',
    });

    // LB1 Final winner (LB1 champion) → LB_SEMI
    tempMatches[4].nextMatchIndex = 6;
    // LB2 winner (LB2 champion) → LB_SEMI
    tempMatches[5].nextMatchIndex = 6;

    // ── Grand Final ───────────────────────────────────────────────────────────
    // GF: match 7 (WB champion + LB_SEMI winner)
    tempMatches.push({
      tournamentId, round: 1, matchNumber: 1, bracketType: 'grand_final', status: 'pending',
    });

    // WB Final winner → GF
    tempMatches[2].nextMatchIndex = 7;
    // LB_SEMI winner → GF
    tempMatches[6].nextMatchIndex = 7;

    // ── Save & Link ───────────────────────────────────────────────────────────
    const savedMatches = await this.saveAndLinkMatches(tempMatches);

    const updatedTournament = await this.tournamentRepository.update(tournamentId, {
      status: 'in_progress',
    });

    return { tournament: updatedTournament, matches: savedMatches };
  }

  private async saveAndLinkMatches(tempMatches: TempMatch[]): Promise<Match[]> {
    const savedMatches: Match[] = [];
    const indexToIdMap: { [key: number]: string } = {};

    for (let i = 0; i < tempMatches.length; i++) {
      const temp = tempMatches[i];
      const saved = await this.matchRepository.create({
        tournamentId: temp.tournamentId,
        round: temp.round,
        matchNumber: temp.matchNumber,
        bracketType: temp.bracketType,
        participant1Id: temp.participant1Id,
        participant2Id: temp.participant2Id,
        status: temp.status,
      });
      savedMatches.push(saved);
      indexToIdMap[i] = saved.id;
    }

    for (let i = 0; i < tempMatches.length; i++) {
      const temp = tempMatches[i];
      const updateData: Partial<Match> = {};

      if (temp.nextMatchIndex !== undefined && indexToIdMap[temp.nextMatchIndex]) {
        updateData.nextMatchId = indexToIdMap[temp.nextMatchIndex];
      }
      if (temp.loserNextMatchIndex !== undefined && indexToIdMap[temp.loserNextMatchIndex]) {
        updateData.loserNextMatchId = indexToIdMap[temp.loserNextMatchIndex];
      }

      if (Object.keys(updateData).length > 0) {
        const updated = await this.matchRepository.update(savedMatches[i].id, updateData);
        savedMatches[i] = updated;
      }
    }

    return savedMatches;
  }
}
